import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Avatar,
  Chip,
  Grid,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import EmailIcon from '@mui/icons-material/Email';
import { getRoleBadgeColor } from '../utils/roleBadge';

interface UserContactDetails {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: string[];
  roleTemplateName?: string;
  managerName?: string;
  departmentName?: string;
  businessUnitName?: string;
  companyName?: string;
}

interface UserContactCardProps {
  open: boolean;
  onClose: () => void;
  user: UserContactDetails | null;
  loading?: boolean;
}

export default function UserContactCard({ open, onClose, user, loading = false }: UserContactCardProps) {
  if (!user && !loading) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="span">
            Contact Information
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography>Loading your details…</Typography>
          </Box>
        ) : user ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  fontSize: '2rem',
                  fontWeight: 'bold',
                }}
              >
                {user.firstName.charAt(0)}{user.lastName.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="600">
                  {user.firstName} {user.lastName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <EmailIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                </Box>
                {user.roles.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {user.roles.map((role) => (
                      <Chip
                        key={role}
                        label={role}
                        size="small"
                        color={getRoleBadgeColor(role)}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={2}>
              {user.roleTemplateName && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <BusinessIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2" color="text.secondary" fontWeight="500">
                      Role Template
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="500">
                    {user.roleTemplateName}
                  </Typography>
                </Grid>
              )}

              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BusinessIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="500">
                    Company
                  </Typography>
                </Box>
                {user.companyName ? (
                  <Typography variant="body1" fontWeight="500">
                    {user.companyName}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Not assigned
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BusinessIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="500">
                    Business Unit
                  </Typography>
                </Box>
                {user.businessUnitName ? (
                  <Typography variant="body1" fontWeight="500">
                    {user.businessUnitName}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Not assigned
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BusinessIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="500">
                    Department
                  </Typography>
                </Box>
                {user.departmentName ? (
                  <Typography variant="body1" fontWeight="500">
                    {user.departmentName}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Not assigned
                  </Typography>
                )}
              </Grid>

              {user.managerName && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SupervisorAccountIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2" color="text.secondary" fontWeight="500">
                      Manager
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="500">
                    {user.managerName}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
